/**
 * Turns an HTTP error (or any thrown error) into a short, user-facing
 * sentence for an inline "couldn't load - tap to retry" state, recognizing
 * the backend's own timeout shape:
 *   { responseCode: "500", responseMessage: "Something Went Wrong",
 *     errorDetails: "Timeout expired. ..." }
 * Shared by every page that shows a per-section/per-KPI retry state, so a
 * timeout reads the same everywhere instead of each page inventing its own
 * detection and wording.
 */
export function isApiTimeout(err: any): boolean {
  const detail =
    (err &&
      err.error &&
      (err.error.errorDetails || err.error.responseMessage)) ||
    "";
  return /timeout/i.test(detail) || (err && err.status === 0);
}

export function apiErrorMessage(err: any): string {
  return isApiTimeout(err)
    ? "Request timed out. Tap refresh to try again."
    : "Couldn't load this data. Tap refresh to try again.";
}

/** Longer title/message for the full-page "couldn't load the report" state
 *  (ReportErrorStateComponent), shown instead of a report's content when its
 *  very first load fails - see each report's `showFullPageError` getter. */
export function reportErrorState(err: any): { title: string; message: string } {
  return {
    title: "We're having trouble loading the report",
    message: isApiTimeout(err)
      ? "The request timed out. This might be due to a slow network or the server taking longer than expected."
      : "Something went wrong while loading this data. Please try again.",
  };
}

/** Same timeout check for a response body that came back through the
 *  success path, e.g. { responseCode: "404", errorDetails: "Timeout expired..." }. */
export function isTimeoutResponse(res: any): boolean {
  const detail = (res && (res.errorDetails || res.responseMessage)) || "";
  return /timeout/i.test(detail);
}
